// ─── Toast.js ──────────────────────────────────────────────────────────────────
// Global toast notification system. Supports success, error, info, warning.
// Usage: showToast('Message', 'success') — dispatches a custom event that
// the ToastContainer listens for.

import React, { useState, useEffect, useCallback } from 'react';
import Icon from './Icons';

const TOAST_DURATION = 4000;
const TOAST_EXIT_DURATION = 200;

let toastId = 0;

// Global function to show a toast from anywhere
export function showToast(message, type = 'info') {
  window.dispatchEvent(new CustomEvent('s2s-toast', {
    detail: { id: ++toastId, message, type },
  }));
}

// Icons for each toast type
const TOAST_ICONS = {
  success: 'check-circle',
  error: 'alert-circle',
  warning: 'alert-triangle',
  info: 'info',
};

export default function ToastContainer() {
  const [toasts, setToasts] = useState([]);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.map(t =>
      t.id === id ? { ...t, exiting: true } : t
    ));
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, TOAST_EXIT_DURATION);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      const toast = e.detail;
      setToasts(prev => [...prev.slice(-4), toast]); // Keep max 5
      setTimeout(() => removeToast(toast.id), TOAST_DURATION);
    };
    window.addEventListener('s2s-toast', handler);
    return () => window.removeEventListener('s2s-toast', handler);
  }, [removeToast]);

  if (toasts.length === 0) return null;

  return (
    <div className="toast-container" role="status" aria-live="polite">
      {toasts.map(t => (
        <div
          key={t.id}
          className={`toast toast-${t.type} ${t.exiting ? 'toast-exit' : ''}`}
          onClick={() => removeToast(t.id)}
        >
          <Icon name={TOAST_ICONS[t.type] || 'info'} size={16} />
          <span>{t.message}</span>
        </div>
      ))}
    </div>
  );
}
